import { FC, useState } from 'react';
import Spacer from '@components/Spacer';
import CheckBox from '@components/FormFields/CheckBox';
import * as tsClient from '@tsClient';
import useNoodleApi from '@hooks/useNoodleApi';
import ProgressIndicator from '@components/ProgressIndicator';
import Sparkle from '@components/Icons/Sparkle';
import * as format from '@format';
import EditButton from '@components/EditButton';

import classes from './WorkflowCard.module.scss';
import { FullWorkflow } from './types';

type WorkflowCardProps = {
  workflow: FullWorkflow;
  canEdit: boolean;
  onEditClick: () => void;
};

const WorkflowCard: FC<WorkflowCardProps> = ({ workflow, canEdit, onEditClick }) => {
  const [isEnabled, setIsEnabled] = useState(workflow.isEnabled);
  const { getData: updateWorkflowFn, fetchingState: updateWorkflowState } = useNoodleApi(tsClient.updateWorkflow, { toastOnError: true });

  const changeWorkflowEnabledState = async (): Promise<void> => {
    await updateWorkflowFn({
      id: workflow.id,
      updates: { isEnabled: !isEnabled },
    });
    setIsEnabled(!isEnabled);
  };

  return (
    <div className={classes.workflowContainer}>
      <div className={classes.header}>
        <Sparkle weight="fill" size="16" />
        <p className="caption-bold">{format.triggerTitle(workflow)}</p>
        {canEdit && (
          <div className={classes.editButton}>
            <EditButton label="Edit" onClick={onEditClick} />
          </div>
        )}
      </div>
      <div>
        <p className="action">{workflow.name}</p>
        <Spacer size="6px" />
        <p className={classes.subText}>{format.actionSubtext(workflow)}</p>
        <Spacer size={'16px'} />
      </div>
      <div className={classes.border} />
      <div className={classes.wrapperToggle}>
        <p className={classes['wrapperToggle-text']}>
          {updateWorkflowState.isFetching && (
            <>
              <ProgressIndicator size={16} />
              {isEnabled ? 'Deactivating...' : 'Activating...'}
            </>
          )}
          {!updateWorkflowState.isFetching && (isEnabled ? 'Active' : 'Inactive')}
        </p>
        <CheckBox
          id={workflow.id}
          isSwitch
          hasFixedHeight={false}
          disabled={workflow.isLocked}
          onChange={() => changeWorkflowEnabledState()}
          values={{ isEnabled }}
          name="isEnabled"
          rules={{ isRequired: true }}
          isSwitchGreenStyle
        />
      </div>
    </div>
  );
};

export default WorkflowCard;
